import React, { ReactNode, useMemo } from 'react';
import classNames from 'classnames';
import { IconRegistry } from 'runtime/registry/icons/IconRegistry';

import './icon.scss';

interface Props {
  name: string;
  className?: string;
  size?: number;
  onClick?: (evn: React.MouseEvent) => void;
  id?: string;
  tooltip?: ReactNode;
  isHtmlTooltip?: boolean;
  tooltipClassName?: string;
  tooltipOffset?: string; // '{"left": 10, top: 10 }'
  tooltipId?: string;
  tooltipPlace?: string;
  disabled?: boolean;
}

export default function Icon({ name, className, size = 16, ...rest }: Props) {
  if (!name) return null;

  const icon = useMemo(() => {
    return IconRegistry.resolve(name);
  }, [name]);

  if (!icon || !icon?.type) {
    console.log(`Icon ${name} is undefined`);
    return;
  }

  switch (icon.type) {
    case 'icomoon':
      return (
        <i
          className={classNames(
            'ui-icon',
            'icomoon',
            `icomoon-${icon.value}`,
            className,
            `fs-${size}`
          )}
          {...rest}
        />
      );

    case 'material':
      return (
        <i
          className={classNames(
            'ui-icon',
            'material-icons',
            className,
            `fs-${size}`
          )}
          {...rest}>
          {icon.value}
        </i>
      );

    case 'svg': {
      const SvgIcon = icon.value;
      return (
        <SvgIcon
          className={classNames('ui-icon', `ui-icon-${name}`, className)}
          width={size}
          height={size}
          {...rest}
        />
      );
    }

    case 'phosphor': {
      const PhosphorIcon = icon.component;
      const isValidReactComponent =
        PhosphorIcon &&
        (typeof PhosphorIcon === 'function' ||
          (typeof PhosphorIcon === 'object' && PhosphorIcon.$$typeof));

      if (!isValidReactComponent) {
        return null;
      }

      return (
        <PhosphorIcon
          width={size}
          height={size}
          className={classNames('ui-icon', `ui-icon_${name}`, `fs-${size}`, className)}
          {...rest} />
      );
    }

    case 'eto': {
      const EtoIcon = icon.component;
      const isValidReactComponent =
        EtoIcon &&
        (typeof EtoIcon === 'function' ||
          (typeof EtoIcon === 'object' && EtoIcon.$$typeof));

      if (!isValidReactComponent) {
        return null;
      }

      return (
        <EtoIcon
          width={size}
          height={size}
          className={classNames('ui-icon', `ui-icon_${name}`, `fs-${size}`, className)}
          {...rest}
        />
      );
    }

    default:
      if (import.meta.env.DEV) {
        console.warn(`[Icon] Unsupported icon type:`, icon);
      }
      return null;
  }
}
